import { computed, ref, watch, type Ref } from 'vue';
import type { CustomFieldEntity } from '@/types/composition-functions';
import { useCustomFields } from '@/composables/queries/useCustomFields';
import { useEntityCustomFieldValues } from '@/composables/queries/useEntityCustomFieldValues';
import { useCustomFieldMutations } from '@/composables/mutations/useCustomFieldMutations';
import { useFormErrors } from '@/composables/useFormErrors';
import {
  buildEditModel, buildValuesPayload, entriesFromDefinitions, missingRequiredErrors, valueErrorKey,
  type CustomFieldInputValue,
} from '@/utils/customFields';

/**
 * Custom field inputs on a form that creates (or edits) the record itself —
 * admit student, new/edit guardian (#574). The values can only be saved once
 * the record has an id, so the form calls validate() before its own save and
 * saveFor(id) after it.
 */
export function useCustomFieldDraft(entity: CustomFieldEntity, options: {
  enabled?: Ref<boolean>;
  /**
   * Set when the form edits an existing record: its stored values are loaded
   * into the inputs and only changed values are sent. Null = a new record.
   */
  recordId?: Ref<string | number | null | undefined>;
} = {}) {
  const recordId = options.recordId ?? ref(null);
  const editing = computed(() => recordId.value !== null && recordId.value !== undefined && recordId.value !== '');

  const { fields, loading } = useCustomFields({ entity, enabled: options.enabled });
  const stored = useEntityCustomFieldValues(entity, recordId, { enabled: options.enabled });
  const { saveEntityValues } = useCustomFieldMutations();
  const { serverErrors, handleServerErrors, clearErrors, getFirstErrorMessage } = useFormErrors();

  const entries = computed(() => (editing.value ? stored.values.value : entriesFromDefinitions(fields.value)));
  const model = ref<Record<string, CustomFieldInputValue>>({});

  // Fill the inputs when the record's values arrive. cache-and-network answers
  // from the cache first, so a fresher copy can follow: refill with it while the
  // inputs still hold the last fill, but never overwrite what is being typed.
  let filledFor: string | null = null;
  let lastFill = '';
  watch([recordId, stored.values], ([id, values]) => {
    const key = id === null || id === undefined ? null : String(id);
    if (key === null) {
      filledFor = null;
      return;
    }
    if (values.length === 0) return;
    if (filledFor === key && JSON.stringify(model.value) !== lastFill) return;
    model.value = buildEditModel(values);
    lastFill = JSON.stringify(model.value);
    filledFor = key;
  }, { immediate: true });

  const clientErrors = ref<Record<string, string>>({});

  const errors = computed<Record<string, string>>(() => Object.fromEntries(entries.value.map(({ field }) => [
    field.key,
    clientErrors.value[field.key] ?? serverErrors.value[valueErrorKey(field.key)]?.[0] ?? '',
  ])));

  /** False (and errors set) when a required field is blank. */
  function validate(): boolean {
    clearErrors();
    clientErrors.value = missingRequiredErrors(entries.value, model.value);
    return Object.keys(clientErrors.value).length === 0;
  }

  /**
   * Save the filled values onto the record. Resolves to null on success (or
   * nothing to save), or to a user-facing message when the save failed —
   * per-field messages also land in `errors`.
   */
  async function saveFor(id: string | number): Promise<string | null> {
    const payload = buildValuesPayload(entries.value, model.value);
    if (payload.length === 0) return null;
    try {
      await saveEntityValues(entity, String(id), payload);
    } catch (error) {
      handleServerErrors(error);
      console.error(error);
      return getFirstErrorMessage() || (error as Error)?.message || 'The additional information could not be saved.';
    }
    // The save returns bare values Apollo cannot match to the cached record, so
    // reload it: reopening this form (or the profile card) must show what was
    // saved, not the old copy. The save itself already succeeded.
    if (editing.value) {
      try {
        await stored.refetch();
      } catch (error) {
        console.error(error);
      }
    }
    return null;
  }

  function reset() {
    model.value = editing.value ? buildEditModel(stored.values.value) : {};
    clientErrors.value = {};
    clearErrors();
  }

  return { entries, model, errors, loading: computed(() => loading.value || stored.loading.value), validate, saveFor, reset };
}
